import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link as RouterLink } from "react-router-dom";
import LogoForDark from "../../assets/brand/logo-for-dark.svg?react";
import { ONBOARDING_COLORS } from "../../features/organisations/onboarding/onboardingDesign.js";
import type { CreateableOrganisationType } from "../isValidOrganisationType.js";
import { brandPanelBasis, fluid } from "./brandPanelSizing.js";
import { GateCollage } from "./GateCollage.js";

/** The roles' brand copies, plus the gate on `/`, which addresses both. */
export type BrandPanelRole = CreateableOrganisationType | "GATE";

/**
 * The wordmark for dark surfaces: the brand panel and the collapsed brand
 * header. It links back to the start page (the gate on `/`), so every signup
 * and invitation screen has a way back.
 */
export const SignupLogo = () => {
  const { t } = useTranslate(["PageSignup"]);

  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label={t("Meister Systems – Home", { ns: "PageSignup" })}
      sx={{
        display: "flex",
        alignSelf: "flex-start",
        width: "fit-content",
        maxWidth: "100%",
        borderRadius: "4px",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "common.white",
          outlineOffset: "4px",
        },
      }}
    >
      <Box
        data-testid="signup-logo"
        sx={{
          display: "flex",
          "& svg": { width: 200, maxWidth: "100%", height: "auto" },
        }}
      >
        <LogoForDark aria-hidden="true" />
      </Box>
    </Box>
  );
};

interface Copy {
  /** Small uppercase line above the headline. */
  eyebrow: string;
  headline: [string, string];
  description: string;
  /** The emphasised line after the description. */
  emphasis?: string;
  benefits: string[];
}

function useBrandCopy(role: BrandPanelRole): Copy {
  const { t } = useTranslate(["PageSignup"]);

  switch (role) {
    case "GATE":
      return {
        eyebrow: t("Craftsmen and clients. Working towards the same goal.", {
          ns: "PageSignup",
        }),
        headline: [
          t("Project enquiry in. Quote out.", { ns: "PageSignup" }),
          t("Working together to get it done.", { ns: "PageSignup" }),
        ],
        description: t(
          "Whether you’re planning a project or carrying out the work, Meister Systems brings everyone together. Information, documents and next steps stay in one place.",
          { ns: "PageSignup" }
        ),
        benefits: [
          t("Keep track of enquiries and quotes", { ns: "PageSignup" }),
          t("Manage questions, photos and documents together", {
            ns: "PageSignup",
          }),
          t("Review and approve quotes online", { ns: "PageSignup" }),
        ],
      };
    case "CLIENT":
      return {
        eyebrow: t("For homeowners and self-builders", { ns: "PageSignup" }),
        headline: [
          t("Your project.", { ns: "PageSignup" }),
          t("As individual as you are.", { ns: "PageSignup" }),
        ],
        description: t(
          "Meister Systems asks the right questions and captures your requirements step by step – by phone, website or online assistant. Without lengthy preparation, your answers provide a clear basis for a quote tailored precisely to your needs.",
          { ns: "PageSignup" }
        ),
        emphasis: t(
          "Turn your idea into a clear project brief. It couldn’t be simpler.",
          { ns: "PageSignup" }
        ),
        benefits: [
          t("Simple questions that capture your individual needs", {
            ns: "PageSignup",
          }),
          t("Clear quotes detailing the work and costs", { ns: "PageSignup" }),
          t("Questions, photos and documents in one place", {
            ns: "PageSignup",
          }),
          t("Approve and sign quotes online", { ns: "PageSignup" }),
        ],
      };
    case "CRAFTSMAN":
      return {
        eyebrow: t("For trade businesses", { ns: "PageSignup" }),
        headline: [
          t("Enquiry in.", { ns: "PageSignup" }),
          t("Compelling quote out.", { ns: "PageSignup" }),
        ],
        description: t(
          "Whether by phone, website or online assistant, Meister Systems brings your customer enquiries together, automatically clarifies customer needs and prepares compelling quotes – with your services and your pricing.",
          { ns: "PageSignup" }
        ),
        emphasis: t("You review and approve. It couldn’t be simpler.", {
          ns: "PageSignup",
        }),
        benefits: [
          t("Customer enquiries from multiple channels in one place", {
            ns: "PageSignup",
          }),
          t("Customer needs and preferences captured automatically", {
            ns: "PageSignup",
          }),
          t("Quotes prepared with your services and prices", {
            ns: "PageSignup",
          }),
          t("Ready for online signing once you approve", {
            ns: "PageSignup",
          }),
        ],
      };
  }
}

/**
 * The dark panel beside the signup and landing forms: the wordmark, a
 * two-line headline (the second line in the accent colour), a description and
 * the benefits of the role.
 */
export const SignupBrandPanel = ({
  signupRole,
}: {
  signupRole: BrandPanelRole;
}) => {
  const { eyebrow, headline, description, emphasis, benefits } =
    useBrandCopy(signupRole);
  // The gate is a little denser than the role panels, to make room for the collage.
  const gate = signupRole === "GATE";

  return (
    <Stack
      component="aside"
      sx={{
        // Board size at 1440px, growing with wider screens (see brandPanelSizing).
        flex: `0 0 ${brandPanelBasis({ width: 560, share: 39 })}`,
        minWidth: 0,
        pt: fluid(48),
        pb: fluid(gate ? 40 : 48),
        px: fluid(56),
        bgcolor: ONBOARDING_COLORS.brandPanel,
        color: "common.white",
      }}
    >
      <SignupLogo />
      {gate && <GateCollage fit="photo" grow sx={{ mt: fluid(40) }} />}
      <Typography
        component="p"
        sx={{
          mt: fluid(gate ? 52 : 72),
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.45,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: ONBOARDING_COLORS.brandAccent,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        component="p"
        sx={{
          mt: fluid(12),
          mb: fluid(gate ? 14 : 16),
          maxWidth: fluid(gate ? 448 : 420),
          fontSize: gate ? 30 : 36,
          fontWeight: 500,
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
        }}
      >
        {headline[0]}
        <br />
        <Box component="span" sx={{ color: ONBOARDING_COLORS.brandAccent }}>
          {headline[1]}
        </Box>
      </Typography>
      <Typography
        sx={{
          maxWidth: fluid(420),
          fontSize: gate ? 15 : 16,
          lineHeight: 1.55,
          letterSpacing: "normal",
          color: "rgba(255, 255, 255, 0.78)",
        }}
      >
        {description}
      </Typography>
      {emphasis && (
        <Typography
          sx={{
            mt: fluid(12),
            maxWidth: fluid(420),
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.55,
            letterSpacing: "normal",
            color: "common.white",
          }}
        >
          {emphasis}
        </Typography>
      )}
      <List
        disablePadding
        sx={{
          mt: fluid(gate ? 28 : 32),
          display: "flex",
          flexDirection: "column",
          gap: fluid(gate ? 10 : 14),
        }}
      >
        {benefits.map(benefit => (
          <ListItem
            key={benefit}
            disableGutters
            sx={{
              p: 0,
              gap: fluid(12),
              alignItems: "center",
              fontSize: gate ? 13 : 14,
              lineHeight: 1.45,
              letterSpacing: "normal",
              color: "rgba(255, 255, 255, 0.85)",
            }}
          >
            <ListItemIcon
              sx={{ color: "rgba(255, 255, 255, 0.7)", minWidth: 0 }}
            >
              <CheckIcon />
            </ListItemIcon>
            <ListItemText
              primary={benefit}
              sx={{ m: 0, flex: "none" }}
              slotProps={{
                primary: {
                  sx: {
                    display: "block",
                    width: "fit-content",
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    letterSpacing: "inherit",
                  },
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    </Stack>
  );
};

/** The design's fine-stroked 20px check. */
const CheckIcon = () => (
  <SvgIcon
    aria-hidden="true"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20 }}
  >
    <path
      d="M5 12.5l4.5 4.5L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);
